import SHA1 from 'crypto-js/sha1'
import _ from 'lodash'
import axios from '@/services/axios'
import cookies from '@/services/cookies'
import router from '@/router'
import { checkTokenExpiration } from 'asokoinsight-tokens/src/helpers'

import { SET_CURRENT_USER, CLEANUP } from './mutation-types'

const state = {
  currentUser: {}
}

const mutations = {
  [SET_CURRENT_USER] (state, user) {
    state.currentUser = user
  },
  [CLEANUP] (state) {
    state.currentUser = {}
  }
}

const sessionHandler = ({ commit, dispatch }, data) => {
  const displayName = data.displayName || data.username
  const publicRoles = data.roles
  const email = data.primaryEmail
  let firstName = _.get(data, 'data.firstName')
  let lastName = _.get(data, 'data.lastName')
  const [givenName, familyName] = (displayName || '').split(' ')
  if (!firstName) {
    firstName = givenName || '<not specified>'
  }
  if (!lastName) {
    lastName = familyName || '<not specified>'
  }

  const user = {
    name: displayName,
    firstName,
    lastName,
    email,
    accessToken: data.token.accessToken,
    intercomHmac: data.data.intercomHmac,
    userId: data.id,
    isActiveSubscriber: false,
    roles: publicRoles,
    interestId: data.data.interestId
  }

  commit(SET_CURRENT_USER, user)
  dispatch('tokens/setTokensData', data.token, { root: true })
  return dispatch('deals/ensure', null, { root: true })
    .then((interest) => {
      dispatch('deals/total', null, { root: true })
      return dispatch('deals/list', { interest }, { root: true })
    })
}

const actions = {
  removeLegacyCookie () {
    const cookie = cookies.getCookie('userInfo')
    if (cookie) {
      cookies.removeCookie('userInfo')
    }
  },
  checkAndRefresh ({ dispatch }) {
    const refreshExpiresAt = localStorage.getItem('refreshExpiresAt')
    let isExpired = false
    if (refreshExpiresAt) {
      isExpired = checkTokenExpiration(refreshExpiresAt)
    }
    if (refreshExpiresAt && !isExpired) {
      dispatch('tokens/refreshAccessToken', null, { root: true })
        .then(() => {
          const path = router.currentRoute.fullPath || '/'
          router.push(path).catch(() => {})
        })
        .catch(err => {
          this._vm.$log(err)
          dispatch('tokens/setTokenChecked', null, { root: true })
          return dispatch('deals/list', null, { root: true })
        })
    } else {
      dispatch('tokens/setTokenChecked', null, { root: true })
      return dispatch('deals/list', null, { root: true })
    }
  },
  init ({ dispatch, getters }) {
    const path = router.currentRoute.fullPath || '/'
    router.push(path).catch(() => {})

    if (!getters.isLoggedIn) {
      return dispatch('deals/list', null, { root: true })
    }

    return dispatch('deals/ensure', null, { root: true })
      .then((interest) => {
        dispatch('deals/total', null, { root: true })
        return dispatch('deals/list', { interest }, { root: true })
      })
  },
  login ({ commit, dispatch }, payload) {
    return axios.post('/users/sessions?v2', payload).then(res => {
      sessionHandler({ commit, dispatch }, res.data)
      dispatch('init')
    })
  },
  handleSessionUpdate ({ commit, dispatch }, data) {
    sessionHandler({ commit, dispatch }, data)
  },
  handleRefreshFail ({ dispatch }, err) {
    this._vm.$log(err)
    dispatch('clearCurrentUser')
    router.push('/')
  },
  logout ({ commit, dispatch }) {
    return axios
      .delete('/users/sessions/me')
      .then(() => {
        dispatch('clearCurrentUser')
        commit(`deals/${CLEANUP}`, null, { root: true })
        dispatch('init')
      })
  },
  register ({ commit, dispatch }, { userData, interest }) {
    const newAccount = { ...userData }

    const emailHash = SHA1(userData.email).toString()
    newAccount.username = `${userData.firstName}_${emailHash}`

    const analyticsUtm = _.pickBy(
      userData,
      (val, key) => _.startsWith(key, 'utm') && val
    )
    if (!_.isEmpty(analyticsUtm)) {
      newAccount.analyticsUtm = analyticsUtm
    }

    return axios.post('/users/new?v2', newAccount).then(res => {
      const user = res.data.user
      const displayName = user.displayName || user.username
      const publicRoles = user.roles
      let firstName = _.get(user, 'data.firstName')
      let lastName = _.get(user, 'data.lastName')
      const [givenName, familyName] = (displayName || '').split(' ')
      if (!firstName) {
        firstName = givenName || 'n/a'
      }
      if (!lastName) {
        lastName = familyName || 'n/a'
      }
      const currentUser = {
        name: displayName,
        firstName,
        lastName,
        email: user.primaryEmail,
        accessToken: res.data.token.accessToken,
        userId: user._id,
        isActiveSubscriber: false,
        roles: publicRoles,
        interestId: (user.data || {}).interestId
      }

      dispatch('tokens/setTokensData', res.data.token, { root: true })

      commit(SET_CURRENT_USER, currentUser)

      interest.userId = currentUser.userId
      return dispatch('deals/create', interest, { root: true }).then(() => {
        return dispatch('init')
      })
    })
  },
  clearCurrentUser ({ commit, dispatch }) {
    commit(CLEANUP)
    dispatch('tokens/clearTokens', null, { root: true })
  }
}

const getters = {
  currentUser: state => state.currentUser,
  isLoggedIn: state => !_.isEmpty(state.currentUser),
  userToken: state => state.currentUser.accessToken,
  userId: state => state.currentUser.userId || 'guest',
  userSubmissionId: state => state.currentUser.interestId
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
